import { Injectable } from "@angular/core";
import { CouponViewModel, CouponDataModel } from "../../shared/models/coupon.model";
import { CoreLogService } from "../../services/log.service";
import { RatingService } from "../../services/rating.service";
import { LikeService } from "../../services/like.service";
import { Observable } from "rxjs";
import { ContentType, ImageOrientation, DateFormatTyps } from "../../shared/contentTypes";
import DateManager from "../../shared/dateManager";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { UserHelperService } from "../../services/userHelper.service";
import { IObjectObserver } from "../../services/IObjectObserver";
import { ObjectObserverFactory } from "../../services/objectObserverFactory";
import { CouponRedemptionService } from "../../services/couponRedemption.service";
import * as moment from "moment";

@Injectable()
export class CouponService {
  private _couponObsServ: IObjectObserver<CouponDataModel>;

  constructor(private _coreLogService: CoreLogService,
    private _ratingService: RatingService,
    private _likeService: LikeService,
    private _userHelperService: UserHelperService,
    private _couponRedemptionService: CouponRedemptionService,
    objObsInjectHelper: CircularInjectionHelper) {
    this._couponObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<CouponDataModel>(ContentType.Coupon, objObsInjectHelper);
  }

  public get ObserveChanges(): Observable<(string | number)[]> {
    return this._couponObsServ.ObserveChanges;
  }

  /** Returns the mapped coupon list for the current user. */
  public GetCouponViewModel(): Promise<CouponViewModel[]> {
    const me = this;
    let isTestUser: boolean = false;
    return new Promise<CouponViewModel[]>((resolve, reject) => {
      isTestUser = me._userHelperService.IsTestUser;
      const currentDate: moment.Moment = moment();
      me._couponObsServ.GetObjectsByFilter(model => !model.deleted
        && model.release && (model.public || isTestUser)
        && model.end_date
        && model.locations.length > 0
        && currentDate.startOf("day").valueOf() <= model.end_date.valueOf())
        .then(data => {
          me.mapCouponModelListToViewModelList(data)
            .then(coupons => {
              resolve(coupons || []);
            })
            .catch(e => {
              me._coreLogService.error("CouponService.GetCouponViewModel", "GetObjectsByFilter.mapCouponModelListToViewModelList", e);
              reject();
            });
        })
        .catch((e) => {
          me._coreLogService.error("CouponService.GetCouponViewModel", "GetObjectsByFilter", e);
          reject();
        });
    });
  }

  /**
    * map a couponDataModel to couponViewModel
    * @param model Get a coupoonDataModel for mapping
    * @result return a couponViewModel
    */
  private async mapDataModelToViewModel(model: CouponDataModel): Promise<CouponViewModel> {
    const me = this;
    const viewModel: CouponViewModel = new CouponViewModel();
    viewModel.id = model.id;
    viewModel.momentFromDate = model.date;
    viewModel.startDate = DateManager.MomentAsString(model.date, DateFormatTyps.DDMMYYYY);
    viewModel.endDate = DateManager.MomentAsString(model.end_date, DateFormatTyps.DDMMYYYY);
    viewModel.startDayMonth = viewModel.startDate.slice(0, 6);
    viewModel.startYear = viewModel.startDate.slice(6);
    viewModel.endDayMonth = viewModel.endDate.slice(0, 6);
    viewModel.endYear = viewModel.endDate.slice(6);
    viewModel.locations = model.locations;
    viewModel.label_de = model.label_de;
    viewModel.label_en = model.label_en;
    viewModel.text_de = model.text_de.replace("\n", "<br>");
    viewModel.text_en = model.text_en.replace("\n", "<br>");
    viewModel.image_photo = ContentType.Coupon + "/" + model.id + "_photo_" + ImageOrientation.Landscape + "/" + model.last_image_update_at_ymd + "/" + model.last_image_update_at_hms;
    try {
      viewModel.like = await me._likeService.HasLike(ContentType.Coupon, model.id);
    } catch (error) {
      me._coreLogService.error("CouponService.mapDataModelToViewModel", "HasLike try catch", error);
    }
    viewModel.locations = model.locations;
    viewModel.anonym_code = model.anonym_code;
    viewModel.isRedeemed = me._couponRedemptionService.isCouponRedeemed(model.id);
    viewModel.isRedeemOncePerUser = model.redeem_once_per_user != null ? model.redeem_once_per_user : false;

    return viewModel;
  }

  /**
  * map a list of couponDataModel to a list of couponViewModel
  * @param model Get a list of couponDataModel for mapping
  * @result return a list of couponViewModel
  */
  private async mapCouponModelListToViewModelList(modelList: CouponDataModel[]): Promise<CouponViewModel[]> {
    const me = this;
    if (modelList && modelList.length > 0) {
      await me._couponRedemptionService.ensureRedeemedCouponsLoaded();
      return Promise.all(modelList.map((model) => me.mapDataModelToViewModel(model)));
    } else {
      return Promise.resolve([]);
    }
  }

}
